import { useMessages } from '../../../shared/i18n/useMessages';
import { SummaryExpander } from '../../../shared/ui/SummaryExpander';
import {
  AUDIT_SUMMARY_BUNDLE,
  type AuditSummaryAudit,
  type AuditSummaryFieldKey,
  type AuditSummaryMessageKey,
} from './auditSummaryCellKeys';
import {
  buildAuditSummaryCells,
  deriveAuditSummaryValues,
  selectAuditSummaryCells,
} from './auditSummaryCells';

export type { AuditSummaryAudit, AuditSummaryFieldKey };

interface AuditSummaryExpanderProps {
  audit: AuditSummaryAudit;
  /**
   * Restrict the panel to these cells, in this order. Omit to show every
   * cell the audit supports.
   */
  fields?: readonly AuditSummaryFieldKey[];
  /** Whether the panel starts open. */
  defaultExpanded?: boolean;
  /**
   * Fixed column count for the metadata grid. Omit on mobile, where the grid
   * fits as many cells as the viewport allows.
   */
  columns?: number;
}

/**
 * Expandable summary card showing audit metadata in a CSS grid layout.
 * Expanded by default. Delegates rendering to shared SummaryExpander and
 * cell construction to `auditSummaryCells`.
 *
 * Cells:
 * - Always: auditId, status (badge), location, businessUnit, division,
 *   auditType, templateId, template, assignees, startDate, expiryDate,
 *   createdBy (when present).
 * - Conditional: score + completed + completedBy for COMPLETED audits;
 *   scheduledDate when the audit has one; the full reopened family
 *   (reopenedBy / reopenedReason / reopenedAt) when the audit has been
 *   reopened at least once.
 *
 * Multi-assignee rendering uses the shared `SummaryAssigneesCell` variant
 * so 0 / 1 / N assignees collapse consistently with the rest of v2.
 *
 * Pass `fields` to show a subset in a caller-chosen order,
 * `defaultExpanded={false}` to start collapsed, and `columns` to fix the grid
 * width on a desktop surface. The Audit Insights tab uses all three to render
 * a short, closed version above its own card.
 */
export function AuditSummaryExpander({
  audit,
  fields,
  defaultExpanded = true,
  columns,
}: AuditSummaryExpanderProps): JSX.Element {
  const { t } = useMessages<AuditSummaryMessageKey>(AUDIT_SUMMARY_BUNDLE);
  const derived = deriveAuditSummaryValues(audit);
  const allCells = buildAuditSummaryCells(audit, t, derived);
  const cells = fields ? selectAuditSummaryCells(allCells, fields) : allCells;
  // `exactOptionalPropertyTypes` rejects an explicit `undefined` for an
  // optional prop, so the omitted case has to be a separate element.
  return columns === undefined ? (
    <SummaryExpander
      titleText={t('summaryTitle')}
      cells={cells}
      defaultExpanded={defaultExpanded}
    />
  ) : (
    <SummaryExpander
      titleText={t('summaryTitle')}
      cells={cells}
      defaultExpanded={defaultExpanded}
      columns={columns}
    />
  );
}
